export class Path {
  /**
   * Internal delimiter token used by this utility.
   * @returns {string}
   */
  static get delim() {
    return 'JdReuPs2V3vy';
  }

  /**
   * @deprecated Use `structuredClone(o)` instead.
   */
  static copy(o) {
    if (o == null || typeof o !== 'object') return o;
    var output, v, key;
    output = Array.isArray(o) ? [] : {};
    for (key in o) {
      v = o[key];
      output[key] = typeof v === 'object' ? Path.copy(v) : v;
    }
    return output;
  }

  /**
   * Escapes special regex characters in a string.
   * @param {string} x - String to escape.
   * @returns {string}
   * @example
   * Path.escape('a.b*c$d'); // => 'a\\.b\\*c\\$d'
   */
  static escape(x) {
    return x.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&');
  }

  /**
   * Compares two paths for exact equality.
   * @param {string|string[]} a - First path.
   * @param {string|string[]} b - Second path.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {boolean}
   * @example
   * Path.equal('a.b.c', 'a.b.c'); // => true
   * Path.equal('a.b', 'a.b.c');   // => false
   */
  static equal(a, b, delim) {
    delim = delim || '.';
    a = Path.pathToArray(a, delim);
    b = Path.pathToArray(b, delim);
    return Path.startsWith(a, b) && a.length == b.length;
  }

  /**
   * Returns a shallow object containing only selected keys.
   * @param {Object} obj - Source object.
   * @param {string[]} keys - Keys to include.
   * @returns {Object}
   * @example
   * Path.getSubset({ a: 1, b: 2, c: 3 }, ['a', 'c']); // => { a: 1, c: 3 }
   */
  static getSubset(obj, keys) {
    let temp = {};
    if (keys == null) return temp;
    for (let key of keys) temp[key] = obj[key];
    return temp;
  }

  /**
   * Joins path parts into a normalized path.
   * @param {Array} parts - Path segments.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string}
   * @example
   * Path.join(['a', 'b', 'c']); // => 'a.b.c'
   * Path.join(['x', 'y'], '/'); // => 'x/y'
   */
  static join(parts, delim) {
    delim = delim || '.';
    return Path.trim(
      parts
        .filter(Path.isValidKey)
        .map((x) => Path.trim(x, delim))
        .join(delim),
      delim
    );
  }

  /**
   * Replaces a path delimiter with another delimiter.
   * @param {string} path - Path to transform.
   * @param {string} delim - Current delimiter.
   * @param {string} newDelim - New delimiter.
   * @returns {string}
   * @example
   * Path.replace('a/b/c', '/', '.'); // => 'a.b.c'
   */
  static replace(path, delim, newDelim) {
    return Path.join(Path.split(path, delim), newDelim);
  }

  /**
   * Creates a shallow copy by merging provided objects.
   * @param {...Object} args - Objects to merge.
   * @returns {Object}
   * @example
   * Path.shallowCopy({ a: 1 }, { b: 2 }); // => { a: 1, b: 2 }
   */
  static shallowCopy(...args) {
    return Object.assign({}, ...args);
  }

  /**
   * Splits a path into normalized segments.
   * @param {string} path - Path to split.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string[]}
   * @example
   * Path.split('a.b.c');      // => ['a', 'b', 'c']
   * Path.split('x/y/z', '/'); // => ['x', 'y', 'z']
   */
  static split(path, delim) {
    delim = delim || '.';
    if (path == null) path = '';
    return Path.clean(path, delim).split(delim);
  }

  /**
   * Removes the first matching subpath from a path.
   * @param {string|string[]} a - Source path.
   * @param {string|string[]} b - Subpath to remove.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string[]}
   * @example
   * Path.subtract('a.b.c.d', 'b.c'); // => ['a', 'd']
   */
  static subtract(a, b, delim) {
    delim = delim || '.';
    a = Path.pathToArray(a, delim);
    b = Path.pathToArray(b, delim);
    let i = a.findIndex((x, i) => b.every((y, j) => a[i + j] == b[j]));
    if (i == -1) return a;
    return [...a.slice(0, i), ...a.slice(i + b.length)];
  }

  /**
   * Checks whether one path starts with another path, supporting `*` wildcards in either path.
   * @param {string|string[]} a - The full path.
   * @param {string|string[]} b - The expected prefix path.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {boolean}
   * @example
   * Path.startsWith('a.b.c', 'a.b'); // => true
   * Path.startsWith('a.b.c', 'a.*'); // => true
   * Path.startsWith('a.b.c', 'x.y'); // => false
   */
  static startsWith(a, b, delim) {
    delim = delim || '.';
    a = Path.pathToArray(a, delim);
    b = Path.pathToArray(b, delim);
    return b.every((x, i) => a[i] == b[i] || (a[i] === '*' && b[i] != null) || (b[i] === '*' && a[i] != null));
  }

  /**
   * Returns values from `a` where `b` contains wildcard segments.
   * @param {string|string[]} a - Concrete path.
   * @param {string|string[]} b - Path pattern containing `*` segments.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string[]}
   * @example
   * Path.getWildcardValues('nodes.abc123.height', 'nodes.*.height'); // => ['abc123']
   */
  static getWildcardValues(a, b, delim) {
    delim = delim || '.';
    a = Path.pathToArray(a, delim);
    b = Path.pathToArray(b, delim);
    // Take parts from b and map them to an array of their index if wild cards and -1 if not.  Remove all -1 values from array and map array to a values at the index of each item.
    return b
      .map((x, i) => (x == '*' ? i : -1))
      .filter((x) => x != -1)
      .map((x) => a[x]);
  }

  /**
   * Replaces wildcard segments in a path with provided values.
   * @param {string|string[]} path - Path pattern containing `*` segments.
   * @param {Array} values - Values to substitute in order.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string}
   * @example
   * Path.setWildcardValues('nodes.*.attributes.*', ['abc123', 'height']); // => 'nodes.abc123.attributes.height'
   */
  static setWildcardValues(path, values, delim) {
    values = Array.isArray(values) ? [...values] : [];
    delim = delim || '.';
    path = Path.pathToArray(path, delim).map((part, i) => {
      if (part == '*') {
        let val = values.shift();
        if (val != null) part = val;
      }
      return part;
    });
    return Path.join(path, delim);
  }

  /**
   * Removes leading and trailing delimiters from a path.
   * @param {*} path - Path value.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string}
   * @example
   * Path.trim('.a.b.c.'); // => 'a.b.c'
   */
  static trim(path, delim) {
    delim = delim || '.';
    if (path == null) path = '';
    if (!isNaN(path)) path = path.toString();
    // Remove leading and trailing delimiters
    return path.replace(new RegExp(`(^\\${delim}*)|(\\${delim}*$)`, 'g'), '');
  }

  /**
   * Normalizes a path by trimming ends and collapsing duplicate delimiters.
   * @param {*} path - Path value.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string}
   * @example
   * Path.clean('.a..b.c.'); // => 'a.b.c'
   */
  static clean(path, delim) {
    delim = delim || '.';
    // First remove leading and trailing delimiters
    // Then remove any duplicate delimiters
    return Path.trim(path, delim).replace(new RegExp(`\\${delim}\\${delim}+`, 'g'), delim);
  }

  /**
   * Checks whether a value is considered empty by this utility.
   * Objects are empty when they have no enumerable keys; other values are empty when null/undefined.
   * @param {*} x - Value to check.
   * @returns {boolean}
   * @example
   * Path.valueIsEmpty(null);  // => true
   * Path.valueIsEmpty({});    // => true
   * Path.valueIsEmpty({ a: 1 }); // => false
   */
  static valueIsEmpty(x) {
    let res;
    if (this.isObject(x)) {
      res = true;
      for (let key in x) {
        res = false;
        break;
      }
    } else {
      res = x == null;
    }
    return res;
  }

  /**
   * Checks whether a value is a non-null object.
   * @param {*} x - Value to check.
   * @returns {boolean}
   * @example
   * Path.isObject({ a: 1 }); // => true
   * Path.isObject(null);     // => false
   * Path.isObject('string'); // => false
   */
  static isObject(x) {
    return typeof x === 'object' && x != null;
  }

  /**
   * Validates that a path contains no null/undefined segments.
   * @param {string|string[]} path - Path to validate.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {boolean}
   * @example
   * Path.isValid('a.b.c'); // => true
   * Path.isValid(null);    // => false
   */
  static isValid(path, delim) {
    delim = delim || '.';
    if (path == null) return false;
    path = Path.pathToArray(path, delim);
    return path.every((x) => x != null);
  }

  /**
   * Returns true when a path key can be safely used as an object property name.
   * Rejects null, empty strings, and object values.
   * @param {*} key - Candidate path key to validate.
   * @returns {boolean}
   * @example
   * Path.isValidKey('name');  // => true
   * Path.isValidKey('');      // => false
   * Path.isValidKey(null);    // => false
   */
  static isValidKey(key) {
    // A key cannot be null or an empty string.
    return key != null && key !== '' && typeof key !== 'object';
  }

  /**
   * Returns references required to set/delete at a path.
   * @param {*} base - Root object.
   * @param {string|string[]} path - Target path.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {{base: Object, item: Object, key: string}}
   * @example
   * const obj = { a: { b: { c: 1 } } };
   * const ref = Path.getPathReference(obj, 'a.b.c');
   * // ref.base === obj      (the root object)
   * // ref.item === obj.a.b  (the direct parent of the final key)
   * // ref.key  === 'c'      (the final path segment)
   */
  static getPathReference(base, path, delim) {
    delim = delim || '.';
    path = Path.pathToArray(path, delim);
    if (!Path.isObject(base)) base = {};
    if (path.length) {
      let lastPathPartIndex = path.length - 1;
      let item = base;
      for (let i = 0; i < lastPathPartIndex; i++) {
        if (!Path.isObject(item[path[i]])) item[path[i]] = {};
        item = item[path[i]];
      }
      return { base, item, key: path[lastPathPartIndex] };
    } else {
      return { base, item: base, key: '' };
    }
  }

  /**
   * Gets a value from an object by path.
   * Supports `*` by selecting the first non-null child at that level.
   * @param {*} base - Root object.
   * @param {string|string[]} path - Path to read.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {*}
   * @example
   * Path.get({ a: { b: { c: 42 } } }, 'a.b.c'); // => 42
   * Path.get({ nodes: { id1: { height: 30 } } }, 'nodes.*.height'); // => 30
   */
  static get(base, path, delim) {
    delim = delim || '.';
    path = Path.pathToArray(path, delim);
    let temp = base;
    for (let i = 0; i < path.length; i++) {
      if (temp == null) {
        // If temp is null (or undefined) then the data this path points to is also null.
        temp = undefined;
        break;
      }
      if (path[i] == '*') {
        let starVal = undefined;
        for (let key in temp) {
          if (temp[key] != null) {
            starVal = temp[key];
            break;
          }
        }
        temp = starVal;
      } else temp = temp[path[i]];
    }
    return temp;
  }

  /**
   * Sets a value on an object by path.
   * @param {*} base - Root object.
   * @param {string|string[]} path - Path to write.
   * @param {*} value - Value to assign.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {{base: *, path: string[], type: 'set'}}
   * @example
   * Path.set({}, 'a.b.c', 42); // => { base: { a: { b: { c: 42 } } }, path: ['a','b','c'], type: 'set' }
   */
  static set(base, path, value, delim) {
    delim = delim || '.';
    path = Path.pathToArray(path, delim);
    let pathRef = Path.getPathReference(base, path, delim);
    if (Path.isValidKey(pathRef.key)) {
      pathRef.item[pathRef.key] = value;
    } else {
      pathRef.base = value;
    }
    return { base: pathRef.base, path, type: 'set' };
  }

  /**
   * Adds a new entry to an update object, but if this path is a subpath of an existing entry, the existing entry is updated.
   * @param {Object} update - The update object.
   * @param {string} path - The update path.
   * @param {*} value - The value to set.
   * @returns {{base: *, path: string[], type: 'set'}}
   * @example
   * // When no ancestor path exists, a new key is added normally:
   * const update = {};
   * Path.setUpdate(update, 'a/b/c', 1);
   * // update => { 'a/b/c': 1 }
   *
   * // When an ancestor path already exists as a key, the value at that key is updated instead:
   * const update2 = { 'a/b': { c: 1 } };
   * Path.setUpdate(update2, 'a/b/d', 2);
   * // update2 => { 'a/b': { c: 1, d: 2 } }  (no new 'a/b/d' key added)
   */
  static setUpdate(update, path, value) {
    const pathParts = Path.split(path, '/');
    for (let i = 0; i < pathParts.length - 1; i++) {
      const subpath = pathParts.slice(0, i + 1).join('/');
      if (update.hasOwnProperty(subpath)) {
        const remainingPath = pathParts.slice(i + 1).join('/');
        return Path.set(update[subpath], remainingPath, value, '/');
      }
    }
    return Path.set(update, path, value);
  }

  /**
   * Deletes a value at a path and collapses empty parent containers.
   * @param {*} base - Root object.
   * @param {string|string[]} path - Path to delete.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {{base: *, path: string[], type: 'delete'}|undefined}
   * @example
   * Path.delete({ a: { b: { c: 1 } } }, 'a.b.c'); // => { base: null, path: [], type: 'delete' }
   */
  static delete(base, path, delim) {
    delim = delim || '.';
    path = Path.pathToArray(path, delim);
    // Warning, this function will also collapse empty object/array paths.
    let pathRef = Path.getPathReference(base, path, delim);
    if (Path.isObject(pathRef)) {
      if (Path.isValidKey(pathRef.key)) {
        delete pathRef.item[pathRef.key];
        // Delete next level up if empty.
        if (Path.valueIsEmpty(pathRef.item)) {
          return Path.delete(pathRef.base, path.slice(0, -1), delim);
        }
      }
      return { base: !path.length || Path.valueIsEmpty(pathRef.base) ? null : pathRef.base, path, type: 'delete' };
    }
  }

  /**
   * Sets or deletes a value depending on emptiness.
   * @param {*} base - Root object.
   * @param {string|string[]} path - Target path.
   * @param {*} value - Value to set; empty values trigger delete.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {{base: *, path: string[], type: 'set'|'delete'}|undefined}
   * @example
   * Path.setAndDelete({ a: { b: 1 } }, 'a.b', 42);   // sets a.b = 42
   * Path.setAndDelete({ a: { b: 1 } }, 'a.b', null); // deletes a.b
   */
  static setAndDelete(base, path, value, delim) {
    if (Path.valueIsEmpty(value)) return Path.delete(base, path, delim);
    return Path.set(base, path, value, delim);
  }

  /**
   * Combines a base path with a relative path and returns the resolved path string.
   * Each `..` segment in the relative path removes one segment from the base path.
   * Preserves protocol prefixes such as `http:` and `https:` when rebuilding the result.
   * @param {string} absolutePath - Base path used as the starting point.
   * @param {string} relativePath - Relative segments to apply to the base path.
   * @param {string} [delim='.'] - Delimiter used to split and rejoin path segments.
   * @returns {string}
   * @example
   * Path.resolve('a.b.c', 'd');    // => 'a.b.c.d'
   * Path.resolve('a/b/z', '../c/d', '/') // => 'a/b/c/d'
   */
  static resolve(absolutePath, relativePath, delim) {
    delim = delim || '.';
    let pathParts = Path.split(absolutePath, delim).filter((x) => x);
    if (pathParts[0] === 'http:' || pathParts[0] === 'https:') pathParts[0] += delim;
    Path.split(relativePath, delim)
      .filter((x) => x)
      .forEach((part) => {
        if (part === '..') pathParts.splice(-1, 1);
        else pathParts.push(part);
      });
    return pathParts.join(delim);
  }

  /**
   * Normalizes diff paths to a string array.
   * Converts null or [""] to [] and stringifies each segment.
   * @param {Array|null|undefined} path - Path array.
   * @returns {string[]}
   * @example
   * Path.sanitizeDiffPath(null);      // => []
   * Path.sanitizeDiffPath(['a', 1]);  // => ['a', '1']
   */
  static sanitizeDiffPath(path) {
    // Change path from null or [""] to [].
    if (path == null || (path.length == 1 && path[0] === '')) path = [];
    return path.map((x) => String(x));
  }

  /**
   * Flattens nested object values into path-keyed entries.
   * @param {Object} data - Object to flatten.
   * @param {string} [delim='.'] - Path delimiter.
   * @param {{path?: string, validate?: function}} [options] - Flattening options.
   * @returns {Object}
   * @example
   * Path.flattenObject({ a: { b: 1, c: 2 } }); // => { 'a.b': 1, 'a.c': 2 }
   */
  static flattenObject(data, delim, options) {
    options = options || {};
    delim = delim || '.';
    let temp = {};
    // Track the current path in options.path
    options.path = options.path || '';
    for (let key in data) {
      const currentPath = options.path ? `${options.path}${delim}${key}` : key;
      if (Path.isObject(data[key])) {
        let flattenedData = Path.flattenObject(data[key], delim, { ...options, path: currentPath });
        for (let path in flattenedData) temp[key + delim + path] = flattenedData[path];
      } else if (typeof options.validate !== 'function' || options.validate(data[key], key, data, currentPath)) {
        temp[key] = data[key];
      }
    }
    return temp;
  }

  /**
   * Converts a path value to an array of segments.
   * @param {string|string[]} path - Path value.
   * @param {string} [delim='.'] - Path delimiter.
   * @returns {string[]}
   * @example
   * Path.pathToArray('a.b.c');         // => ['a', 'b', 'c']
   * Path.pathToArray(['a', 'b', 'c']); // => ['a', 'b', 'c']
   */
  static pathToArray(path, delim) {
    delim = delim || '.';
    if (!Array.isArray(path)) path = Path.split(path, delim);
    if (path.length == 1 && path[0] == '') path = [];
    return path;
  }

  /**
   * Converts empty values to null.
   * @param {*} x - Value to normalize.
   * @returns {*|null}
   * @example
   * Path.nullify(null); // => null
   * Path.nullify({});   // => null
   * Path.nullify(42);   // => 42
   */
  static nullify(x) {
    return Path.valueIsEmpty(x) ? null : x;
  }
}
